import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, BookOpen, Briefcase, Rocket, ShieldCheck, Sparkles, Trophy, Users } from 'lucide-react';
import useInView from '../hooks/useInView';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.1);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const studioSignals = [
  { icon: BookOpen, title: 'Prepare ideas', text: 'Children can build concepts, portfolios, presentations and project confidence before submissions open.' },
  { icon: Users, title: 'Build teams', text: 'Clubs, challenges and Academy activity can help children practice collaboration inside one safe ecosystem.' },
  { icon: Trophy, title: 'Compete for support', text: 'Selected projects may receive up to $25,000 in MVP funding support after ROMIND review.' },
  { icon: ShieldCheck, title: 'Parents stay involved', text: 'Any formal development terms are handled with parents or legal guardians while the child remains the creator.' },
];

const VentureStudioTeaserSection = () => (
  <section data-testid="venture-studio-teaser-section" className="relative py-24 bg-[#05050A] overflow-hidden">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[620px] h-[320px] bg-purple-600/10 rounded-full blur-[130px] pointer-events-none animate-nebula" />
    <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />
    <div
      className="absolute inset-0 pointer-events-none opacity-35"
      style={{
        background:
          'radial-gradient(circle at 16% 24%, rgba(0,240,255,0.12), transparent 28%), radial-gradient(circle at 82% 20%, rgba(157,76,221,0.18), transparent 30%), radial-gradient(circle at 55% 86%, rgba(255,0,127,0.10), transparent 34%)',
      }}
    />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        <FadeIn className="lg:col-span-6">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-300 mb-3 font-outfit">
            ROMIND Venture Studio
          </p>
          <h2 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.04] tracking-tight mb-5">
            From young ideas to
            <span className="gradient-text"> real projects.</span>
          </h2>
          <p className="text-white/58 text-base sm:text-lg leading-relaxed max-w-2xl mb-6">
            ROMIND KIDS is being built as one safe social-creative ecosystem, not a bundle of disconnected features. Venture Studio is the future layer where children can grow from clubs, Academy, challenges and portfolio work into serious project preparation.
          </p>
          <p className="text-white/50 text-sm sm:text-base leading-relaxed max-w-2xl mb-8">
            Planned for June 2027, ROMIND Venture Studio will allow eligible active members to submit project ideas for ROMIND review. Selected projects may receive up to $25,000 in MVP funding support to test a first version or proof of viability.
          </p>

          <div className="flex flex-wrap gap-3">
            <Link
              to="/venture-studio"
              data-testid="venture-studio-teaser-cta"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-purple-600/80 border border-purple-400/45 text-white hover:bg-purple-500 hover:shadow-[0_0_28px_rgba(157,76,221,0.24)] transition-all duration-200"
            >
              Explore Venture Studio <ArrowRight size={14} />
            </Link>
            <span className="inline-flex items-center px-6 py-3 rounded-full font-semibold text-sm bg-white/5 border border-white/12 text-white/55 select-none">
              Coming June 2027
            </span>
          </div>
        </FadeIn>

        <FadeIn delay={120} className="lg:col-span-6">
          <div className="glass-card p-5 sm:p-6 lg:p-7 glow-border">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-xs font-outfit font-semibold text-white/40 uppercase tracking-widest mb-2">
                  Future Project Fund
                </p>
                <h3 className="font-outfit font-bold text-2xl sm:text-3xl text-white tracking-tight">
                  A path children can prepare for now.
                </h3>
              </div>
              <div className="hidden sm:flex w-12 h-12 rounded-2xl bg-white/5 border border-white/10 items-center justify-center flex-shrink-0">
                <Rocket size={22} className="text-cyan-300" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
              {studioSignals.map((item, index) => (
                <div
                  key={item.title}
                  data-testid={`venture-studio-signal-${index}`}
                  className="rounded-2xl bg-white/[0.035] border border-white/10 p-4 transition-all duration-200 hover:border-cyan-400/25 hover:bg-white/[0.055]"
                >
                  <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-3">
                    <item.icon size={17} className="text-purple-300" />
                  </div>
                  <h4 className="font-outfit font-semibold text-white text-sm mb-1.5">{item.title}</h4>
                  <p className="text-white/43 text-xs leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl bg-[#05050A]/55 border border-white/10 p-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500/10 border border-cyan-300/20 px-3 py-1 text-[11px] font-semibold text-cyan-200">
                  <Sparkles size={12} /> Submissions open July 2027
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-500/10 border border-purple-300/20 px-3 py-1 text-[11px] font-semibold text-purple-200">
                  <Briefcase size={12} /> 6+ months active membership
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-[11px] font-semibold text-white/55">
                  <BadgeCheck size={12} /> ROMIND review process
                </span>
              </div>
              <p className="text-white/50 text-sm leading-relaxed">
                Submission access creates an opportunity to be reviewed. Project selection and funding are not automatic and will depend on preparation, potential, feasibility and ROMIND evaluation.
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

export default VentureStudioTeaserSection;
